import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Contact } from "../../../models";
import { ApiClient } from "../../../network/ApiClient";
import { ApiEndpoints } from "../../../network/ApiEndpoints";

type ContactsInitialState = {
  isLoading: boolean;
  data: Contact[];
  error: string;
  metadata?: {
    count: number;
    offset: number;
    limit: number;
  };
};

const initialState: ContactsInitialState = {
  isLoading: false,
  data: [],
  error: "",
};

type ContactsMetadata = { count: number; offset: number; limit: number };
type ContactsEnvelope = { data?: { data?: Contact[]; metadata?: ContactsMetadata } };

// GET /contact caps Limit at 100 server-side (GetContactsQueryHandler), so a single request can't
// return a large address book — it would silently stop at the first page and those contacts would
// never surface as share suggestions. Page through with Offset until every contact is collected.
export const getContacts = createAsyncThunk<{
  data: Contact[];
  metadata?: ContactsMetadata;
}>("getContacts", async () => {
  const pageSize = 100; // matches the server's MaxPageSize
  const all: Contact[] = [];
  let offset = 0;
  let total = 0;

  // Bounded loop (≤100 pages / 10k contacts) so a misbehaving envelope can't spin forever.
  for (let page = 0; page < 100; page++) {
    const response = await ApiClient.get<ContactsEnvelope>(
      ApiEndpoints.getContacts(),
      { Limit: pageSize, Offset: offset },
      {}
    );
    const batch = response?.data?.data;
    if (!Array.isArray(batch)) {
      // An empty address book and a payload we failed to understand look identical to the
      // user — both mean "no suggestions" — so say which one this was. The envelope's shape
      // is what diagnoses it; the contents are the user's contacts and do not belong in a
      // console. Return whatever pages we already collected.
      console.error(
        "GET /contact returned an unexpected payload; envelope keys:",
        Object.keys(response?.data ?? {})
      );
      break;
    }
    all.push(...batch);
    total = response?.data?.metadata?.count ?? all.length;
    offset += pageSize;
    if (batch.length < pageSize || all.length >= total) {
      break;
    }
  }

  return { data: all, metadata: { count: total, offset: 0, limit: all.length } };
});

export const getContactsSlice = createSlice({
  name: "contacts",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getContacts.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(
      getContacts.fulfilled,
      (
        state,
        action: PayloadAction<{
          data: Contact[];
          metadata?: { count: number; offset: number; limit: number };
        }>
      ) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.metadata = action.payload.metadata;
        state.error = "";
      }
    );
    builder.addCase(getContacts.rejected, (state, action) => {
      state.isLoading = false;
      state.data = [];
      state.error = action.error.message || "Something went wrong";
    });
  },
});

export const getContactsReducer = getContactsSlice.reducer;
